import { EmailDraft } from '../models/types';

/**
 * Service for storing and retrieving email drafts from LocalStorage
 */
export class EmailStorageService {
  private readonly STORAGE_KEY = 'email_drafts';
  
  /**
   * Saves an email draft to LocalStorage
   * @param draft The email draft to save
   * @returns The ID of the saved draft
   */
  public saveEmailDraft(draft: EmailDraft): string {
    const drafts = this.getAllDrafts();
    drafts.push(draft);
    this.saveDraftsToStorage(drafts);
    return draft.id;
  }
  
  /**
   * Retrieves an email draft from LocalStorage by ID
   * @param id The ID of the draft to retrieve
   * @returns The email draft or null if not found
   */
  public getEmailDraft(id: string): EmailDraft | null {
    const drafts = this.getAllDrafts();
    const draft = drafts.find(d => d.id === id);
    return draft || null;
  }
  
  /**
   * Updates an existing email draft
   * @param id The ID of the draft to update
   * @param changes The changes to apply to the draft
   * @returns The updated draft or null if not found
   */
  public updateEmailDraft(id: string, changes: Partial<EmailDraft>): EmailDraft | null {
    const drafts = this.getAllDrafts();
    const index = drafts.findIndex(d => d.id === id);
    
    if (index === -1) {
      return null;
    }
    
    const updatedDraft = { ...drafts[index], ...changes };
    drafts[index] = updatedDraft;
    this.saveDraftsToStorage(drafts);
    
    return updatedDraft;
  }
  
  /**
   * Lists all email drafts
   * @returns Array of email drafts
   */
  public listEmailDrafts(): EmailDraft[] {
    return this.getAllDrafts();
  }
  
  /**
   * Deletes an email draft
   * @param id The ID of the draft to delete
   * @returns true if deleted, false if not found
   */
  public deleteEmailDraft(id: string): boolean {
    const drafts = this.getAllDrafts();
    const index = drafts.findIndex(d => d.id === id);
    
    if (index === -1) {
      return false;
    }
    
    drafts.splice(index, 1);
    this.saveDraftsToStorage(drafts);
    
    return true;
  }
  
  /**
   * Retrieves all drafts from LocalStorage
   * @returns Array of email drafts
   */
  private getAllDrafts(): EmailDraft[] {
    try {
      const draftsJson = localStorage.getItem(this.STORAGE_KEY);
      
      if (!draftsJson) {
        return [];
      }
      
      const drafts = JSON.parse(draftsJson);
      
      // Convert string dates back to Date objects
      return drafts.map((draft: any) => ({
        ...draft,
        createdAt: new Date(draft.createdAt),
        meetingInfo: {
          ...draft.meetingInfo,
          meetingDate: new Date(draft.meetingInfo.meetingDate),
          endDate: draft.meetingInfo.endDate ? new Date(draft.meetingInfo.endDate) : undefined,
          activities: draft.meetingInfo.activities.map((activity: any) => ({
            ...activity,
            startDate: new Date(activity.startDate),
            endDate: activity.endDate ? new Date(activity.endDate) : undefined
          }))
        }
      }));
    } catch (error) {
      console.error('Error retrieving email drafts from LocalStorage:', error);
      return [];
    }
  }
  
  /**
   * Saves drafts to LocalStorage
   * @param drafts The drafts to save
   */
  private saveDraftsToStorage(drafts: EmailDraft[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(drafts));
    } catch (error) {
      console.error('Error saving email drafts to LocalStorage:', error);
    }
  }
}